// MedicationLogSheet — teacher-only sheet to record that a dose was
// just administered. Append-only — there is no edit path for teachers.
//
// Snapshots the medication name + authorized dosage onto the log row
// so the entry is meaningful even if the parent later edits the
// underlying consent.

import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Pill, Warning, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  formatAdministeredAt,
  listAdministrationsForAuthorization,
  logAdministration,
  routeLabel,
} from "@/lib/medications";

function nowLocalForDateTimeInput() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function MedicationLogSheet({
  open,
  onOpenChange,
  authorization, // full row (active + valid)
  child,         // {id, organization_id, first_name, last_name}
  administeredBy, // teacher profile id
  onSaved,
}) {
  const [doseGiven, setDoseGiven] = useState("");
  const [observations, setObservations] = useState("");
  const [administeredAt, setAdministeredAt] = useState(nowLocalForDateTimeInput());
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    if (!open || !authorization?.id) return;
    setDoseGiven(authorization?.dosage || "");
    setObservations("");
    setAdministeredAt(nowLocalForDateTimeInput());
    listAdministrationsForAuthorization(authorization.id, { limit: 5 })
      .then(setRecent)
      .catch(() => setRecent([]));
  }, [open, authorization?.id, authorization?.dosage]);

  const onSubmit = async () => {
    if (!doseGiven.trim()) {
      toast.error("Dose given is required");
      return;
    }
    setBusy(true);
    try {
      const row = await logAdministration({
        organizationId: child.organization_id,
        childId: child.id,
        authorization,
        administeredAt: new Date(administeredAt).toISOString(),
        administeredBy,
        doseGiven,
        observations,
      });
      toast.success("Dose recorded");
      onSaved?.(row);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.message || "Could not record dose");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="medication-log-sheet"
        side="bottom"
        className="max-h-[92dvh] overflow-y-auto rounded-t-3xl border-t border-neutral-200 sm:max-w-md sm:mx-auto"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl flex items-center gap-2">
            <Pill size={18} weight="duotone" className="text-primary-hover" />
            Record dose given
          </SheetTitle>
          <SheetDescription>
            For{" "}
            <strong className="text-neutral-900">
              {[child?.first_name, child?.last_name].filter(Boolean).join(" ")}
            </strong>{" "}
            · {authorization?.medication_name}
          </SheetDescription>
        </SheetHeader>

        {/* Consent snapshot — read-only */}
        <section
          data-testid="medication-log-consent-summary"
          className="mt-4 rounded-2xl border border-primary/20 bg-primary/5 p-3 text-[12px] text-neutral-800"
        >
          <p className="text-[10px] font-bold uppercase tracking-wider text-primary-hover">
            Authorised by parent
          </p>
          <p className="mt-1">
            <strong>{authorization?.medication_name}</strong> · {authorization?.dosage} · {routeLabel(authorization?.route)}
          </p>
          {authorization?.schedule && (
            <p className="mt-0.5 text-neutral-700">{authorization.schedule}</p>
          )}
          {authorization?.warnings && (
            <p className="mt-1 inline-flex items-start gap-1 rounded-xl border border-amber-300 bg-amber-50 px-2 py-1 text-[12px] text-amber-900">
              <Warning size={12} weight="duotone" className="mt-0.5" aria-hidden />
              {authorization.warnings}
            </p>
          )}
        </section>

        <div className="mt-4 grid grid-cols-1 gap-3">
          <Field label="Dose actually given" required>
            <input
              type="text"
              data-testid="med-log-dose"
              value={doseGiven}
              onChange={(e) => setDoseGiven(e.target.value)}
              placeholder={authorization?.dosage || "5 ml"}
              className={INPUT_CLS}
            />
          </Field>
          <Field label="When">
            <input
              type="datetime-local"
              data-testid="med-log-when"
              value={administeredAt}
              onChange={(e) => setAdministeredAt(e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          <Field label="Observations">
            <textarea
              rows={2}
              data-testid="med-log-obs"
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder="took it without fuss"
              className={INPUT_CLS}
            />
          </Field>
        </div>

        {recent.length > 0 && (
          <section className="mt-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
              Recent doses for this medication
            </p>
            <ul className="mt-1 space-y-1 text-[12px] text-neutral-700">
              {recent.map((r) => (
                <li
                  key={r.id}
                  data-testid={`med-log-recent-${r.id}`}
                  className="rounded-xl border border-neutral-200 bg-white px-2 py-1"
                >
                  <strong>{r.dose_given}</strong> · {formatAdministeredAt(r.administered_at)}
                  {r.administrator?.full_name ? ` · ${r.administrator.full_name}` : ""}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            data-testid="med-log-cancel"
            onClick={() => onOpenChange?.(false)}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            <X size={12} weight="bold" />
            Cancel
          </button>
          <button
            type="button"
            data-testid="med-log-save"
            disabled={busy}
            onClick={onSubmit}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
          >
            {busy ? "Saving…" : "Record dose"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const INPUT_CLS =
  "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

function Field({ label, required, help, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
        {label}
        {required && <span className="ml-0.5 text-[#C6851E]">*</span>}
      </span>
      {children}
      {help && <p className="mt-1 text-[11px] text-neutral-500">{help}</p>}
    </label>
  );
}
